import React, {useRef, useState} from 'react';
import {CircularProgress, IconButton} from '@mui/joy';
import {useTranslation} from 'react-i18next';
import {AddAPhotoRounded, HideImageRounded, PhotoCameraRounded} from '@mui-symbols-material/w300';
import OptionsMenu, {OptionsMenuOption} from '../OptionsMenu';
import {NodeImageTarget, useNodeImage} from '../../hooks/useNodeImage';

/**
 * Class for the element wrapping a nameplate image: the action button is
 * hidden until that wrapper is hovered / focused (always visible on touch).
 */
export const NODE_IMAGE_HOVER_TARGET_CLASS = 'node-image-hover-target';

interface Props {
    /** From buildNodeImageTarget; renders nothing without write access. */
    target: NodeImageTarget | null | undefined;
}

/**
 * Small round camera button in the bottom-right corner of a 200px nameplate
 * image. Without an image it opens the native picker (images only) directly;
 * with an image set it opens a two-entry menu — replace or remove. Changes are
 * saved immediately. Must be rendered as a *sibling* of the clickable image
 * box, inside a `position: relative` wrapper carrying NODE_IMAGE_HOVER_TARGET_CLASS.
 */
const NodeImageActionButton = ({target}: Props) => {
    const {t} = useTranslation();
    const {setNodeImage, uploadAndSetNodeImage, busy} = useNodeImage();
    const inputRef = useRef<HTMLInputElement>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    if (!target?.canWrite) {
        return null;
    }

    const openPicker = () => inputRef.current?.click();

    // A menu only earns its place with more than one option: without an image
    // the button goes straight to the picker.
    const hasImage = !!target.currentImageId;
    const options: OptionsMenuOption[] = hasImage ? [
        {
            icon: <AddAPhotoRounded fontSize="small"/>,
            title: t('Replace image'),
            onClick: openPicker,
        },
        {
            icon: <HideImageRounded fontSize="small"/>,
            title: t('Remove image'),
            onClick: () => { void setNodeImage(target, null); },
        },
    ] : [];

    const menuOpen = Boolean(anchorEl);

    return (
        <>
            <IconButton
                variant="soft"
                color="neutral"
                size="sm"
                aria-label={hasImage ? t('Replace image') : t('Upload image')}
                aria-haspopup={hasImage ? 'true' : undefined}
                aria-expanded={hasImage && menuOpen ? 'true' : undefined}
                data-testid="node-image-action"
                disabled={busy}
                onClick={(event) => {
                    event.stopPropagation();
                    if (hasImage) {
                        setAnchorEl(event.currentTarget);
                    } else {
                        openPicker();
                    }
                }}
                sx={{
                    position: 'absolute',
                    right: 6,
                    bottom: 6,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    boxShadow: 'sm',
                    // Hidden until the image is hovered / focused (keyboard) — same
                    // pattern as .ib-row-open-detail in the tree column. Touch
                    // devices have no hover, so it stays visible there.
                    opacity: menuOpen || busy ? 1 : 0,
                    transition: 'opacity 0.2s',
                    [`.${NODE_IMAGE_HOVER_TARGET_CLASS}:hover &, .${NODE_IMAGE_HOVER_TARGET_CLASS}:focus-within &`]: {
                        opacity: 1,
                    },
                    '@media (hover: none)': {opacity: 1},
                    '&:hover': {backgroundColor: '#fff'},
                }}
            >
                {busy ? <CircularProgress size="sm"/> : <PhotoCameraRounded/>}
            </IconButton>
            <OptionsMenu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={() => setAnchorEl(null)}
                options={options}
            />
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                hidden
                data-testid="node-image-action-file-input"
                onChange={(event) => {
                    const file = event.target.files?.[0];
                    // allow re-picking the same file
                    event.target.value = '';
                    if (file) {
                        void uploadAndSetNodeImage(target, file);
                    }
                }}
            />
        </>
    );
};

export default NodeImageActionButton;
